import ArticleIcon from '@mui/icons-material/Article';
import ErrorIcon from '@mui/icons-material/Error';
import { Skeleton, Table } from '@mui/joy';
import clsx from 'clsx';
import * as React from 'react';

import { Button } from './button';

const hadKeyboardEvent = true;

/**
 * Computes whether the given element should automatically trigger the
 * `focus-visible` class being added, i.e. whether it should always match
 * `:focus-visible` when focused.
 * @param {Element} node
 * @return {boolean}
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function focusTriggersKeyboardModality(node) {
  const { type, tagName } = node;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (tagName === 'INPUT' && inputTypesWhitelist[type] && !node.readOnly) {
    return true;
  }

  if (tagName === 'TEXTAREA' && !node.readOnly) {
    return true;
  }

  if (node.isContentEditable) {
    return true;
  }

  return false;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export function isFocusVisible(event) {
  try {
    return event.target.matches(':focus-visible');
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);
    // browsers not implementing :focus-visible will throw a SyntaxError
    // we use our own heuristic for those browsers
    // rethrow might be better if it's not the expected error but do we really
    // want to crash if focus-visible malfunctioned?
  }

  // no need for validFocusTarget check. the user does that by attaching it to
  // focusable events only
  return hadKeyboardEvent || focusTriggersKeyboardModality(event.target);
}

const DIVIDER = Object.freeze({
  horizontal: 'horizontal',
  vertical: 'vertical',
  grid: 'grid',
  clean: 'clean',
} as const);

const SIZE = Object.freeze({
  compact: 'compact',
  default: 'default',
  spacious: 'spacious',
} as const);

function isObject(value: unknown): value is object {
  return Object.prototype.toString.call(value) === '[object Object]';
}

type Size = keyof typeof SIZE;
type Divider = keyof typeof DIVIDER;

type TableBuilderProps<RowT> = {
  children?: React.ReactNode;
  data: Array<RowT>;
  divider?: Divider;
  horizontalScrollWidth?: string;
  sortColumn?: string | null;
  sortOrder?: 'ASC' | 'DESC' | null;
  onSort?: (columnId: string) => void;
  isLoading?: boolean;
  loadingMessage?: React.ReactNode | (() => React.ReactNode);
  emptyMessage?: React.ReactNode | (() => React.ReactNode);
  size?: Size;
  hoverRow?: boolean;
  noHeader?: boolean;
  headerOnly?: boolean;
};

type TableBuilderColumnProps<RowT> = {
  children: (row: RowT, rowIndex?: number) => React.ReactNode;
  id?: string;
  header?: React.ReactNode;
  numeric?: boolean;
  sortable?: boolean;
  tableHeadAriaLabel?: string;
  style?: React.CSSProperties;
  noWrap?: boolean;
};

export class TableBuilderColumn<T> extends React.Component<
  TableBuilderColumnProps<T>
> {
  // Renderless component. See TableBuilder.
}

export class TableBuilder<T> extends React.Component<
  TableBuilderProps<T>,
  {
    fixed?: boolean;
  }
> {
  static defaultProps = {
    data: [],
    loadingMessage: 'Loading...',
  };

  state = {
    fixed: false,
  };


  render() {
    const {
      children,
      data,
      isLoading,
      loadingMessage,
      emptyMessage,
      hoverRow,
      noHeader,
      headerOnly,
    } = this.props;

    const columns = React.Children.toArray(children)
      .filter(Boolean)
      // @ts-expect-error todo(flow->ts): limit children to be only react elements
      .map((child) => child.props);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    function renderHeader({ header, ...col }, colIndex) {
      return (
        <th {...col} className='whitespace-nowrap' key={colIndex}>
          {header}
        </th>
      );
    }

    function renderCell(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      { style, noWrap, ...col },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      row,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      rowIndex,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      colIndex,
    ) {
      return (
        <td
          key={colIndex}
          style={style}
          className={clsx([noWrap ? 'whitespace-nowrap' : ''])}
        >
          {col.children(row, rowIndex)}
        </td>
      );
    }

    const isEmpty = !isLoading && data.length === 0;
    const isRendered = !isLoading && !isEmpty;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hasId = (col: unknown): col is { id: any } => {
      return isObject(col) && 'id' in col && !!col.id;
    };

    return (
      <div className='relative overflow-auto scale-100'>
        <Table
          size='md'
          stickyHeader
          variant='plain'
          sx={{
            '--TableCell-cornerRadius': (theme) => theme.radius.xs,
            '--TableCell-paddingX': (theme) => theme.spacing(2),
            '--TableCell-paddingY': (theme) => theme.spacing(1.5),
            '& tr th': {
              color: (theme) => theme.palette.neutral[400],
            },
            tableLayout: this.state.fixed ? 'fixed' : 'auto',
          }}
          className='min-w-full'
          hoverRow={hoverRow}
        >
          {!noHeader && (
            <thead>
              <tr>
                {columns.map((col, colIndex) => renderHeader(col, colIndex))}
              </tr>
            </thead>
          )}
          {!headerOnly && (
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={columns.length}>
                    <div>
                      {typeof loadingMessage === 'function'
                        ? loadingMessage()
                        : loadingMessage}
                    </div>
                  </td>
                </tr>
              )}
              {isEmpty && emptyMessage && (
                <tr>
                  <td colSpan={columns.length}>
                    <div>
                      {typeof emptyMessage === 'function'
                        ? emptyMessage()
                        : emptyMessage}
                    </div>
                  </td>
                </tr>
              )}
              {isRendered &&
                data.map((row, rowIndex) => (
                  <tr key={hasId(row) ? row.id : rowIndex}>
                    {columns.map((col, colIndex) =>
                      renderCell(
                        col,
                        row,
                        hasId(row) ? row.id : rowIndex,
                        colIndex,
                      ),
                    )}
                  </tr>
                ))}
            </tbody>
          )}
        </Table>
      </div>
    );
  }
}

type Row = {
  id: number | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
};

type TableProps<T = Row> = TableBuilderProps<T> & {
  isError?: boolean;
  isEmpty?: boolean;
  onRefetch?: VoidFunction;
};

type ArrayItem<T> = T extends (infer U)[] ? U : never;

type TableColumnData<T> = T extends Array<unknown> ? ArrayItem<T> : T;

export default function createTableBuilder<
  T,
  ColumnData = TableColumnData<T>,
>() {
  return {
    Table: ({
      onRefetch,
      isEmpty: _isEmpty,
      isError,
      ...props
    }: TableProps<ColumnData>) => {
      const isDataEmpty = props.data.length === 0;
      return (
        <TableBuilder<ColumnData>
          loadingMessage={<Skeleton />}
          {...props}
          emptyMessage={
            isError ? (
              <div className='flex flex-col items-center space-y-2 px-4 py-5 text-center text-sm'>
                <ErrorIcon
                  aria-hidden='true'
                  className='text-brand-danger-500'
                />
                <div>
                  <h3 className='font-bold text-brand-danger-500'>
                    Failed to load data
                  </h3>
                  <p className='text-brand-danger-500'>
                    Failed to load data for this table
                  </p>
                </div>
                {!!onRefetch && (
                  <div>
                    <Button size='md' onClick={onRefetch}>
                      Try Again
                    </Button>
                  </div>
                )}
              </div>
            ) : isDataEmpty ? (
              props.emptyMessage ?? (
                <div className='flex flex-col items-center space-y-2 px-4 py-5 text-center text-sm'>
                  <ArticleIcon aria-hidden='true' className='text-gray-400' />
                  <div>
                    <h3 className='font-bold text-gray-500'>No Data</h3>
                    <p className='text-gray-500'>
                      No data found. Update your filter or refresh the page to
                      try again.
                    </p>
                  </div>
                </div>
              )
            ) : null
          }
        />
      );
    },
    Column: (props: TableBuilderColumnProps<ColumnData>) => (
      <TableBuilderColumn<ColumnData> {...props} />
    ),
    useTableSelection: (initialValue?: Record<string, boolean>) => {
      const [selected, setSelected] = React.useState<Record<string, boolean>>(
        () => initialValue ?? {},
      );
      const items = Object.values(selected).filter(Boolean);
      const hasAny = Boolean(items.length);
      const hasAll = hasAny && items.every((item) => !!item);
      const hasSome = hasAny && items.some((item) => !!item);

      const isSelected = (id: string) => Boolean(selected[id]);

      function toggleAll() {
        setSelected((data) => {
          for (const key in data) {
            data[key] = !hasAll;
          }
          return { ...data };
        });
      }

      function toggle(args: { key: string; value: boolean }) {
        setSelected((data) => {
          data[args.key] = false;
          for (const key in data) {
            if (key === args.key) {
              data[key] = args.value;
            }
          }
          return { ...data };
        });
      }

      function reset() {
        setSelected({});
      }

      return {
        hasAny,
        hasAll,
        hasSome,
        selected,
        isSelected,
        toggleAll,
        toggle,
        reset,
      };
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useTableBuilder<T>(_data?: T) {
  return React.useState(() => createTableBuilder<T>())[0];
}
